<template>
  <div class="user-search-statistic">
      <div class="user-search-field">
          <ModelString label="Поиск пользователей" v-model="searchString" />

          <span class="help-outline" @click="searchHelpPopupOpened = true" />

          <Modal :opened="searchHelpPopupOpened" @close="searchHelpPopupOpened = false">
              <UserSearchHelp />
          </Modal>
      </div>
      <UserSearchTagsCloud :selectedTags="selectedTags" @selectTag="addTags" />

      <div class="user-search-statistic__main">
          <UserSearchFilters
              v-if="!isMobile"
              :filters-data="getUserSearchFilters"
              :is-favorite="!isFavorite"
              :with-drafts="withDrafts"
              :is-important-fields-filled="isImportantFieldsFilled"
              @submit="addFilters"
          />
          <template v-else>
              <Button class="button" @click="openFilters">Фильтры</Button>
              <Modal :opened="filterPopupOpened" keep @close="filterPopupOpened = false">
                  <UserSearchFilters
                      :filters-data="getUserSearchFilters"
                      :is-favorite="!isFavorite"
                      :with-drafts="withDrafts"
                      :is-important-fields-filled="isImportantFieldsFilled"
                      @submit="addFilters"
                  />
              </Modal>
          </template>
          <div class="user-search-statistic__list">
              <FilterChips
                  class="user-search-list__chips"
                  :chipsItems="filterChips"
                  @removeItem="removeFilter"
                  @clear="clearFilters"
              />
              <div class="user-search-statistic__wrapper">
                  <component
                    v-for="currentComponent in currentShownComponents"
                    :key="currentComponent"
                    :is="currentComponent"
                    :apolloSearchString="apolloSearchString" 
                    :userSearchFilters="getUserSearchFilters"
                    :selectedTags="selectedTags"
                  >
                  </component>
              </div>
          </div>
      </div>
  </div>
</template>

<script>
import { graphQueryConstructor, lexParse } from '@/tools/graphQueryConstructor';
import ModelString from '@/components/UI/models/ModelString';
import UserSearchTagsCloud from './userSearchTagsCloud.vue';
import UserSearchFilters from './UserSearchFilters.vue';
import gql from 'graphql-tag';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import qs from 'qs';
import FilterChips from '@/components/UI/FilterChips.vue';
import str_counter from '@/tools/str_counter';
import { getDefaultFilters } from './filters';
import Modal from '@/components/UI/Modal.vue';
import UserSearchHelp from '@/components/user-search/UserSearchHelp.vue';
import debounce from '@/tools/debounce';

import ParticipantRegistrationsChart from './charts/ParticipantRegistrationsChart';
import LastActivityChart from './charts/LastActivityChart';
import UserDistributionChart from './charts/UserDistributionChart';
import AgeGroupsChart from './charts/AgeGroupsChart';
import GenderCompositionChart from './charts/GenderCompositionChart';
import HighEducationChart from './charts/HighEducationChart';
import EducationLevelChart from './charts/EducationLevelChart';
import CompetenceChart from './charts/CompetenceChart';
import CoursesChart from './charts/CoursesChart';
import ProjectDistributionByMonthChart from './charts/ProjectDistributionByMonthChart';
import ProjectDistributionChart from './charts/ProjectDistributionChart';

export default {
  name: 'UserSearchList',

  components: {
      UserSearchHelp,
      FilterChips,
      ModelString,
      UserSearchFilters,
      UserSearchTagsCloud,
      UserDistributionChart,
      Modal,
      ParticipantRegistrationsChart,
      LastActivityChart,
      AgeGroupsChart,
      GenderCompositionChart,
      HighEducationChart,
      EducationLevelChart,
      CompetenceChart,
      CoursesChart,
      ProjectDistributionByMonthChart,
      ProjectDistributionChart,
  },

  props: {
      isFavorite: {
          type: Boolean,
          default: true,
      },
      withDrafts: {
          type: Boolean,
          default: true,
      },
      isImportantFieldsFilled: {
          type: Boolean,
          default: true,
      },
      statisticType: {
        type: String,
      },
  },
  apollo: {
      $skipAll: true,
      users: {
          query() {
              let searchData;
              const options = {
                  withoutContacts: !this.hasUserContactsAccess,
              };

              try {
                  searchData = lexParse(this.apolloSearchString, options);
                  this.searchStringError = false;
              } catch (e) {
                  searchData = '';

                  this.searchStringError =
                      e.message === 'token not recognized' ? 'В запросе присутствует недопустимый символ' : e.message;

                  console.error(e);
              }
              return gql(graphQueryConstructor(this.getUserSearchFilters, searchData, options, this.selectedTags));
          },
          variables: {
              skip: 0,
          },
      },
  },
  data() {
      return {
          filterPopupOpened: false,
          searchHelpPopupOpened: false,
          searchString: '',
          selectedTags: [],
          tagsObjectByIdKey: {},
          apolloSearchString: '',
          skip: 0,
          showMore: true,
          filters: getDefaultFilters(),
          resultUsers: [],
          noRouteChange: false,
          usersProcessed: [],
          prevFilter: '',
      };
  },

  computed: {
      ...mapGetters([
          'isMobile',
          'dictionaryByType',
          'getAllProjectsList',
          'getRootProjectsList',
          'getUserSearchFilters',
          'getUserMainSearchString',
      ]),
      ...mapGetters('projects', ['getSeasons', 'getSeasonsCached']),
      ...mapGetters('auth', ['hasUserContactsAccess']),
      ...mapGetters('data', ['getRegistryItems']),
      sanitizedFilters() {
          return this.sanitizeData(this.getUserSearchFilters);
      },
      currentShownComponents() {
        if(this.statisticType === 'generalStatistic') {
          return [
            'participant-registrations-chart', 'last-activity-chart', 'user-distribution-chart', 'age-groups-chart',
            'gender-composition-chart',
          ];
        } else if(this.statisticType === 'educationStatistic') {
          return ['high-education-chart',  'competence-chart', 'courses-chart', 'education-level-chart'];
        }else if(this.statisticType === 'projectStatistics') {
          return ['project-distribution-chart',  'project-distribution-by-month-chart'];
        }else {
          return "";
        }
      },
      filtersQuery() {
          const tags = this.selectedTags.map((tag) => tag.id);
          const search = this.getUserMainSearchString;
          const filters = this.sanitizedFilters;

          const queryParams = {};

          if (tags.length) {
              queryParams.tags = tags;
          }

          if (search) {
              queryParams.search = search;
          }

          if (filters && Object.keys(filters)) {
              queryParams.filters = filters;
          }
          return qs.parse(qs.stringify(queryParams));
      },
      filtersQueryString() {
          const search = this.getUserMainSearchString;
          const filters = this.sanitizedFilters;
          const tags = this.selectedTags.map((tag) => tag.id);

          const queryParams = {};

          if (search) {
              queryParams.search = search;
          }
          if (tags.length) {
              queryParams.tags = tags;
          }
          if (filters && Object.keys(filters)) {
              queryParams.filters = filters;
          }

          return qs.stringify(queryParams);
      },

      filterChips() {
          const chips = [];

          const profile = this.getUserSearchFilters.profile || {};
          const lang = this.getUserSearchFilters.language || {};
          const nominations = this.getUserSearchFilters.nominations || {};
          const education = this.getUserSearchFilters.education || {};
          const career = this.getUserSearchFilters.career || {};
          const course = this.getUserSearchFilters.course || {};

          const ageText = str_counter('', 'год', 'года', 'лет');
          const getDictValueById = (type, id) => {
              const item = this.dictionaryByType(type).find((v) => v.id === parseInt(id));
              return item ? item.field_value : id;
          };

          if (this.getUserMainSearchString) {
              chips.push({
                  id: `search`,
                  index: -1,
                  field_value: this.getUserMainSearchString,
                  type: 'search',
              });
          }

          (this.selectedTags || []).forEach((item, index) => {
              chips.push({
                  id: `tags_${index}`,
                  index,
                  field_value: item.field_value,
                  type: 'tag',
              });
          });

          if (course.courseDateFrom) {
              chips.push({
                  id: `course_courseDateFrom`,
                  index: -1,
                  field_value: `Обучение на курсах РСВ от ${course.courseDateFrom}`,
                  type: 'course.courseDateFrom',
              });
          }

          if (course.courseDateTo) {
              chips.push({
                  id: `course_courseDateTo`,
                  index: -1,
                  field_value: `Обучение на курсах РСВ до ${course.courseDateTo}`,
                  type: 'course.courseDateTo',
              });
          }

          (course.foreign_institutes_id || []).forEach((item, index) => {
              chips.push({
                  id: `course_foreign_institutes_${index}`,
                  index,
                  field_value: item.name,
                  type: 'course.foreign_institutes_id',
              });
          });

          (course.event_id || []).forEach((item, index) => {
              chips.push({
                  id: `course_event_${index}`,
                  index,
                  field_value: item.name,
                  type: 'course.event_id',
              });
          });

          (course.courseStatus || []).forEach((item, index) => {
              chips.push({
                  id: `course_status_${index}`,
                  index,
                  field_value: item.name,
                  type: 'course.courseStatus',
              });
          });

          (profile.addresses || []).forEach((item, index) => {
              chips.push({
                  id: `profile_addresses_${index}`,
                  index,
                  field_value: item.value,
                  type: 'profile.addresses',
              });
          });
          (profile.citizenship || []).forEach((item, index) => {
              chips.push({
                  id: `profile_citizenship_${index}`,
                  index,
                  field_value: item,
                  type: 'profile.citizenship',
              });
          });
          (profile.skills || []).forEach((item, index) => {
              chips.push({
                  id: `profile_skills_${index}`,
                  index,
                  field_value: getDictValueById('key_skills', item),
                  type: 'profile.skills',
              });
          });
          (profile.jobStatuses || []).forEach((item, index) => {
              chips.push({
                  id: `profile_jobStatuses_${index}`,
                  index,
                  field_value: getDictValueById('job_status', item),
                  type: 'profile.jobStatuses',
              });
          });
          (profile.lifts || []).forEach((item, index) => {
              chips.push({
                  id: `profile_lifts_${index}`,
                  index,
                  field_value: getDictValueById('social_lift', item),
                  type: 'profile.lifts',
              });
          });
          (profile.activities || []).forEach((item, index) => {
              chips.push({
                  id: `profile_activities_${index}`,
                  index,
                  field_value: getDictValueById('industries', item),
                  type: 'profile.activities',
              });
          });

          if (profile.ageGt) {
              chips.push({
                  id: `profile_ageGt`,
                  index: -1,
                  field_value: `От ${profile.ageGt} ${ageText(profile.ageGt)}`,
                  type: 'profile.ageGt',
              });
          }
          if (profile.ageLt) {
              chips.push({
                  id: `profile_ageLt`,
                  index: -1,
                  field_value: `До ${profile.ageLt} ${ageText(profile.ageLt)}`,
                  type: 'profile.ageLt',
              });
          }
          if (profile.registrationDateFrom) {
              chips.push({
                  id: `profile_registrationDateFrom`,
                  index: -1,
                  field_value: `Зарегистрирован от ${profile.registrationDateFrom}`,
                  type: 'profile.registrationDateFrom',
              });
          }
          if (profile.registrationDateTo) {
              chips.push({
                  id: `profile_registrationDateTo`,
                  index: -1,
                  field_value: `Зарегистрирован до ${profile.registrationDateTo}`,
                  type: 'profile.registrationDateTo',
              });
          }
          if (profile.withDrafts !== null) {
              chips.push(
                  profile.withDrafts
                      ? {
                            id: `profile_withDrafts`,
                            index: -1,
                            field_value: 'Только черновики',
                            type: 'profile.withDrafts',
                        }
                      : {
                            id: `profile_withDrafts`,
                            index: -1,
                            field_value: 'Без учета черновиков',
                            type: 'profile.withDrafts',
                        }
              );
          }
          if (profile.importantFieldsFilled !== null) {
              chips.push(
                  profile.importantFieldsFilled
                      ? {
                            id: `profile_importantFieldsFilled`,
                            index: -1,
                            field_value: 'Входят в ФП «Соцлифты для каждого»',
                            type: 'profile.importantFieldsFilled',
                        }
                      : {
                            id: `profile_importantFieldsFilled`,
                            index: -1,
                            field_value: 'Не входят в ФП «Соцлифты для каждого»',
                            type: 'profile.importantFieldsFilled',
                        }
              );
          }

          if (profile.fromSalary) {
              chips.push({
                  id: `profile_fromSalary`,
                  index: -1,
                  field_value: `От ${profile.fromSalary} ₽`,
                  type: 'profile.fromSalary',
              });
          }
          if (profile.toSalary) {
              chips.push({
                  id: `profile_toSalary`,
                  index: -1,
                  field_value: `До ${profile.toSalary} ₽`,
                  type: 'profile.toSalary',
              });
          }

          if (profile.government) {
              chips.push({
                  id: `profile_government`,
                  index: -1,
                  field_value: profile.government === 'true' ? 'Опыт на гос. службе' : 'Без опыта на гос. службе',
                  type: 'profile.government',
              });
          }

          if (profile.volunteerWork) {
              chips.push({
                  id: `profile_volunteerWork`,
                  index: -1,
                  field_value:
                      profile.volunteerWork === 'true'
                          ? 'Участие в волонтерских программах'
                          : 'Без участия в волонтерских программах',
                  type: 'profile.volunteerWork',
              });
          }
          if (profile.socialWork) {
              chips.push({
                  id: `profile_socialWork`,
                  index: -1,
                  field_value:
                      profile.socialWork === 'true'
                          ? 'Участие в социальной работе'
                          : 'Без участия в социальной работе',
                  type: 'profile.socialWork',
              });
          }
          if (profile.gender) {
              chips.push({
                  id: `profile_gender`,
                  index: -1,
                  field_value: profile.gender === '1' ? 'Пол: мужской' : 'Пол: женский',
                  type: 'profile.gender',
              });
          }

          (nominations.rootProjects || []).forEach((item, index) => {
              const project = this.getRootProjectsList.find((v) => v.id === item) || { name: `Проект id=${item}` };

              if (project) {
                  chips.push({
                      id: `nominations_rootProjects_${index}`,
                      index,
                      field_value: project.name,
                      type: 'nominations.rootProjects',
                  });
              }
          });
          (nominations.participantStatuses || []).forEach((item, index) => {
              chips.push({
                  id: `nominations_participantStatuses_${index}`,
                  index,
                  field_value: getDictValueById('stage_history', item),
                  type: 'nominations.participantStatuses',
              });
          });
          (nominations.projectsTypes || []).forEach((item, index) => {
              chips.push({
                  id: `nominations_projectsTypes_${index}`,
                  index,
                  field_value: item.name,
                  type: 'nominations.projectsTypes',
              });
          });
          (nominations.projects || []).forEach((item, index) => {
              const project =
                  parseInt(item).toString() === item.toString()
                      ? this.getSeasons.find((v) => v.id.toString() === item.toString()) || {
                            name: `Проект id=${item}`,
                        }
                      : this.getAllProjectsList.find((v) => v.id === item) || { name: `Проект id=${item}` };
              if (project) {
                  chips.push({
                      id: `nominations_projects_${index}`,
                      index,
                      field_value: project.name,
                      type: 'nominations.projects',
                  });
              }
          });

          if (nominations.projectRegisteredAtDateFrom) {
              chips.push({
                  id: `nominations_projectRegisteredAtDateFrom`,
                  index: -1,
                  field_value: `Регистрация в проект от ${nominations.projectRegisteredAtDateFrom}`,
                  type: 'nominations.projectRegisteredAtDateFrom',
              });
          }

          if (nominations.projectRegisteredAtDateTo) {
              chips.push({
                  id: `nominations_projectRegisteredAtDateTo`,
                  index: -1,
                  field_value: `Регистрация в проект до ${nominations.projectRegisteredAtDateTo}`,
                  type: 'nominations.projectRegisteredAtDateTo',
              });
          }

          const nominationsSpk1 = Object.values(this.getSeasonsCached)
              .map((v) => {
                  return v.nominations.map((n) => ({ ...n, name: `${n.name} (${v.name})` }));
              })
              .flat();
          const nominationsSpk2 = this.getAllProjectsList
              .map((v) => {
                  return v.nominations.map((n) => ({ ...n, name: `${n.name} (${v.name})` }));
              })
              .flat();

          (nominations.seasons || []).forEach((item, index) => {
              const nomination =
                  parseInt(item).toString() === item.toString()
                      ? nominationsSpk1.find((v) => v.id.toString() === item.toString()) || {
                            name: `Номинация id=${item}`,
                        }
                      : nominationsSpk2.find((v) => v.id === item) || { name: `Номинация id=${item}` };
              if (nomination) {
                  chips.push({
                      id: `nominations_season_${index}`,
                      index,
                      field_value: nomination.name,
                      type: 'nominations.seasons',
                  });
              }
          });

          if (nominations.nominationRegisteredAtDateFrom) {
              chips.push({
                  id: `nominations_nominationRegisteredAtDateFrom`,
                  index: -1,
                  field_value: `Регистрация в номинацию от ${nominations.nominationRegisteredAtDateFrom}`,
                  type: 'nominations.nominationRegisteredAtDateFrom',
              });
          }

          if (nominations.nominationRegisteredAtDateTo) {
              chips.push({
                  id: `nominations_nominationRegisteredAtDateTo`,
                  index: -1,
                  field_value: `Регистрация в номинацию до ${nominations.nominationRegisteredAtDateTo}`,
                  type: 'nominations.nominationRegisteredAtDateTo',
              });
          }

          (education.educationLevels || []).forEach((item, index) => {
              chips.push({
                  id: `education_educationLevels_${index}`,
                  index,
                  field_value: getDictValueById('education_level', item),
                  type: 'education.educationLevels',
              });
          });
          (education.educationPlaces || []).forEach((item, index) => {
              chips.push({
                  id: `education_educationPlaces_${index}`,
                  index,
                  field_value: item,
                  type: 'education.educationPlaces',
              });
          });
          (education.educationSpecialties || []).forEach((item, index) => {
              chips.push({
                  id: `education_educationSpecialties_${index}`,
                  index,
                  field_value: getDictValueById('education_specialities', item),
                  type: 'education.educationSpecialties',
              });
          });
          (education.educationDegrees || []).forEach((item, index) => {
              chips.push({
                  id: `education_educationDegrees_${index}`,
                  index,
                  field_value: getDictValueById('academic_degree', item),
                  type: 'education.educationDegrees',
              });
          });

          (career.levels || []).forEach((item, index) => {
              chips.push({
                  id: `career_levels_${index}`,
                  index,
                  field_value: getDictValueById('position_level', item),
                  type: 'career.levels',
              });
          });

          if (lang.language) {
              chips.push({
                  id: 'language_language',
                  index: -1,
                  field_value: `Язык: ${getDictValueById('languages', lang.language)}`,
                  type: 'language.language',
              });
          }
          if (lang.languageLevel) {
              chips.push({
                  id: 'language_languageLevel',
                  index: -1,
                  field_value: `Уровень владения: ${getDictValueById('level_language', lang.languageLevel)}`,
                  type: 'language.languageLevel',
              });
          }

          return chips;
      },
  },

  watch: {
      $route(route) {
          if (!route || route.params.noRouteChange || this.noRouteChange) {
              return;
          }

          this.noRouteChange = true;

          const routeQuery = route.query;
          const queryString = Object.keys(routeQuery)
              .map((key) => `${key}=${routeQuery[key]}`)
              .join('&');
          const queryData = qs.parse(queryString) || {};

          this.setUserSearchFilters({ mode: 'default'});

          this.setUserMainSearchString('');
          this.searchString = '';
          this.selectedTags = [];

          if (queryData.filters && Object.keys(queryData.filters).length > 0) {
              for (const type in queryData.filters) {
                  for (const key in queryData.filters[type]) {
                      this.setUserSearchFilters({
                          mode: 'setWithType',
                          key: key,
                          filter: queryData.filters[type][key],
                          type: type,
                      });
                  }
              }
          }

          if (queryData.search) {
              this.setUserMainSearchString(queryData.search);
              this.searchString = queryData.search;
          }

          if (queryData.tags ** queryData.tags.length) {
              this.selectedTags = queryData.tags;
          }

          this.$nextTick(() => {
              this.noRouteChange = false;
              delete this.$route.params.noRouteChange;
              this.$apollo.skipAll = false;
          });
      },
      async users(users) {
          const idsArray = [];
          users.items.forEach((user) => {
              idsArray.push(user.id);
          });

          await this.loadUsersData(idsArray);

          this.$emit('total-count', users.totalCount);
      },
      searchString: debounce(
          function (newVal) {
              this.setUserMainSearchString(newVal);
              this.apolloSearchString = newVal;
              this.skip = 0;
              this.applyRouteQuery();
          },
          500,
          false,
          'user-search-string'
      ),
  },

  methods: {
      ...mapActions(['loadUsersData']),
      ...mapActions('data', ['loadRegistryItems']),
      ...mapMutations(['setUserSearchFilters', 'setUserMainSearchString']),
      applyRouteQuery() {
          if (this.noRouteChange) {
              return;
          }
          if (this.isFavorite) {
              this.getUserSearchFilters.favorite_membership.isFavorite = true;
              this.getUserSearchFilters.favorite_membership.status = true;
          }
          const query = this.filtersQueryString;

          const getPairs = (obj, keys = []) =>
              Object.entries(obj).reduce((pairs, [key, value]) => {
                  if (typeof value === 'object') pairs.push(...getPairs(value, [...keys, key]));
                  else pairs.push([[...keys, key], value]);
                  return pairs;
              }, []);

          const route = {
              name: this.$route.name,
              query: Object.fromEntries(
                  getPairs(this.filtersQuery).map(([[key0, ...keysRest], value]) => [
                      `${key0}${keysRest.map((a) => `[${a}]`).join('')}`,
                      value,
                  ])
              ),
              params: {
                  ...this.$route.params,
                  noRouteChange: true,
              },
          };

          const fullUrl = this.$router.resolve(route).href;
          const currentUrl = `${window.location.pathname}${window.location.search}`;

          if (currentUrl !== fullUrl) {
              window.rsvNoRouteScroll = true;
              this.$router.push(route).catch((err) => {});
              window.rsvNoRouteScroll = false;
          }
      },
      sanitizeData(obj) {
          return Object.entries(obj)
              .filter(([_, v]) => v != null && v !== '')
              .reduce((acc, [k, v]) => ({ ...acc, [k]: v === Object(v) ? this.sanitizeData(v) : v }), {});
      },

      refetchAppolloUsers(noSkipAll = false) {
          let curFilter = JSON.parse(JSON.stringify(this.getUserSearchFilters));
          delete curFilter.nominations.rootProjectsList;
          curFilter = JSON.stringify(curFilter);

          if (curFilter === this.prevFilter) {
              return;
          }

          this.prevFilter = curFilter;

          this.applyRouteQuery();
          this.resultUsers = [];
          this.$nextTick(() => {
              if (noSkipAll) {
                  this.$apollo.skipAll = false;
              }
              this.$apollo.queries.users.refetch();
          });
      },

      addTags(tag) {
          if (tag.selected) {
              this.selectedTags.push({ field_value: tag.field_value, id: String(tag.id), selected: tag.selected });
          } else if (tag.selected === false) {
              this.selectedTags.forEach((item, index) => {
                  if (item.id == tag.id) {
                      this.selectedTags.splice(index, 1);
                  }
              });
          }
          this.refetchAppolloUsers();
      },
      addFilters(e) {
          for (const key in e) {
              this.setUserSearchFilters({
                  mode: 'setWithoutType',
                  key: key,
                  filter: e[key],
                  type: null,
              });
          }

          this.refetchAppolloUsers();
      },
      loadMoreUsers() {
          this.skip += 20;
          this.$apollo.queries.users.fetchMore({
              variables: {
                  skip: this.skip,
              },
              updateQuery: (previousResult, { fetchMoreResult }) => {
                  const newUsers = fetchMoreResult.users.items;
                  const newTotalCount = fetchMoreResult.users.totalCount;
                  const newPageInfo = fetchMoreResult.users.pageInfo;
                  return {
                      users: {
                          __typename: previousResult.users.__typename,
                          items: [...previousResult.users.items, ...newUsers],
                          totalCount: newTotalCount,
                          pageInfo: newPageInfo,
                      },
                  };
              },
          });
      },
      clearFilters() {
          this.$apollo.skipAll = true;
          const newFilters = getDefaultFilters();

          for (const key in newFilters) {
              this.setUserSearchFilters({
                  mode: 'setWithoutType',
                  key: key,
                  filter: newFilters[key],
                  type: null,
              });
          }

          this.setUserMainSearchString('');
          this.searchString = '';
          this.selectedTags = [];

          this.refetchAppolloUsers(true);
      },
      removeFilter(item) {
          if (item.type === 'search') {
              this.setUserMainSearchString('');
              this.searchString = '';
              return;
          }
          if (item.type === 'tag') {
              this.selectedTags.splice(item.index, 1);
          } else {
              const [type, key] = item.type.split('.');

              if (item.index === -1) {
                  this.setUserSearchFilters({
                      mode: 'setWithType',
                      key: key,
                      filter: null,
                      type: type,
                  });
              } else {
                  const items = [...this.getUserSearchFilters[type][key]];
                  items.splice(item.index, 1);
                  this.setUserSearchFilters({
                      mode: 'setWithType',
                      key: key,
                      filter: items,
                      type: type,
                  });
              }
          }

          this.refetchAppolloUsers(true);
      },

      openFilters() {
          this.filterPopupOpened = true;
      },
  },
  async created() {
      this.noRouteChange = true;
      this.searchString = this.getUserMainSearchString;
      await this.loadRegistryItems({ type: 'user_tags', withMeta: true });
      const userTags = this.getRegistryItems('user_tags');
      userTags.forEach((tag) => (this.tagsObjectByIdKey[tag.id] = tag));
      const routeQuery = this.$route.query;
      const queryString = Object.keys(routeQuery)
          .map((key) => `${key}=${routeQuery[key]}`)
          .join('&');
      const queryData = qs.parse(queryString) || {};

      if (queryData.filters && Object.keys(queryData.filters).length > 0) {
          const filters = queryData.filters;

          if (filters.profile) {
              if (filters.profile.withDrafts) {
                  filters.profile.withDrafts = filters.profile.withDrafts === 'true';
              }
              if (filters.profile.importantFieldsFilled) {
                  filters.profile.importantFieldsFilled = filters.profile.importantFieldsFilled === 'true';
              }
          }

          if (filters.favorite_membership) {
              filters.favorite_membership.membership_owner_id = filters.favorite_membership.membership_owner_id
                  ? parseInt(filters.favorite_membership.membership_owner_id)
                  : null;
              filters.favorite_membership.status = filters.favorite_membership.status === 'true';

              filters.favorite_membership.isFavorite = this.isFavorite;
          } else {
              filters.favorite_membership = {
                  status: true,
                  isFavorite: this.isFavorite,
              };
          }

          for (const type in filters) {
              for (const key in filters[type]) {
                  this.setUserSearchFilters({
                      mode: 'setWithType',
                      key: key,
                      filter: filters[type][key],
                      type: type,
                  });
              }
          }
      }

      if (queryData.search) {
          this.setUserMainSearchString(queryData.search);
          this.searchString = queryData.search;
      }

      if (queryData.tags && queryData.tags.length) {
          queryData.tags.forEach((tagId) => {
              userTags.forEach((userTag) => {
                  if (tagId == userTag.id) {
                      this.selectedTags.push({
                          field_value: userTag.field_value,
                          id: String(userTag.id),
                          selected: true,
                      });
                  }
              });
          });
      }
      this.setUserSearchFilters({
          mode: 'setDefinite',
          key: 'favorite_membership.isFavorite',
          value: false,
      });

      this.setUserSearchFilters({
          mode: 'setDefinite',
          key: 'favorite_membership.status',
          value: true,
      });
      
      this.$nextTick(() => {
          this.noRouteChange = false;
          this.$apollo.skipAll = false;
      });
  },
};
</script>
<style lang="scss" scoped>
@import '~@/styles/rem';
@import '~@/styles/variables';
@import '~@/styles/buttons_v2';

.user-search-statistic {
  font-family: $font-montserrat;


  &__wrapper {
    display: flex;
    flex-direction: column;
    gap: rem(80);
  }
  .user-search-field {
      position: relative;

      .help-outline {
          cursor: pointer;
          display: block;
          position: absolute;
          width: rem(32);
          height: rem(32);
          right: rem(16);
          top: 50%;
          margin-top: rem(-16);
          background: url('~@/assets/images/help-outline.svg') no-repeat center center;
          background-size: cover;
      }
  }

  &__loader {
      position: absolute;
      background: rgba(255, 255, 255, 0.8);
      z-index: 100;
      top: mrem(-10);
      left: mrem(-10);
      right: mrem(-10);
      bottom: mrem(-10);
      border-radius: rem(16);

      @include on-desktop {
          top: rem(-10);
          left: rem(-10);
          right: rem(-10);
          bottom: rem(-10);
          border-radius: rem(16);
      }

      :deep(.bouncing-loader) {
          position: absolute;
          left: 50%;
          top: mrem(200);
          transform: translateX(-50%);

          @include on-desktop {
              top: rem(200);
          }
      }
  }

  &__lists {
      position: relative;
      min-height: mrem(600);

      @include on-desktop {
          min-height: rem(600);
      }
  }

  &__lists-wrapper {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: mrem(40);

      @include on-desktop {
          gap: rem(40);
      }
  }

  &__main {
      margin-top: mrem(60);
      display: flex;
      flex-direction: column;
      gap: mrem(40);

      @include on-desktop {
          margin-top: rem(60);
          display: grid;
          grid-template-columns: rem(356) auto;
          gap: rem(40);
      }
  }

  &__cards {
      display: flex;
      flex-direction: column;
      row-gap: mrem(20);

      @include on-desktop {
          row-gap: rem(20);
      }

      &-load-more {
          display: flex;
          cursor: pointer;
          color: #fff;
          justify-content: center;
          align-items: center;
          border: none;
          background: $color-main-blue;
          padding: mrem(12) mrem(59);
          border-radius: mrem(10);
          gap: mrem(10);

          @include on-desktop {
              padding: rem(12) rem(59);
              border-radius: rem(10);
              gap: rem(10);
          }

          &-icon {
              width: rem(22);
              height: rem(16);
          }

          &-title {
              display: flex;
              align-items: center;
              justify-content: center;
              font-style: normal;
              font-weight: 600;
              font-size: mrem(16);
              line-height: mrem(26);

              @include on-desktop {
                  font-size: rem(16);
                  line-height: rem(26);
              }
          }
      }
  }

  &__search-error {
      background-color: $color-main-red;
      color: #fff;
      font-style: normal;
      font-weight: 400;
      padding: mrem(12) 0 mrem(12) mrem(26);
      font-size: mrem(16);
      width: 100%;
      line-height: mrem(24);
      margin-bottom: mrem(60);

      @include on-desktop {
          padding: rem(12) 0 rem(12) rem(26);
          font-size: rem(16);
          width: rem(1192);
          line-height: rem(24);
          margin-bottom: rem(60);
      }
  }

  &__no-result {
      background-color: $color-main-red;
      background-color: #f7f9fc;
      color: #2a2626;
      font-style: normal;
      font-weight: 400;
      padding: mrem(12) 0 mrem(12) mrem(26);
      font-size: mrem(16);
      width: 100%;
      line-height: mrem(24);

      @include on-desktop {
          padding: rem(12) 0 rem(12) rem(26);
          font-size: rem(16);
          width: rem(1192);
          line-height: rem(24);
      }
  }

  &__chips {
      @include on-mobile {
          margin-bottom: mrem(40);
      }
  }
}

::v-deep {
  .mt-input {
      border-radius: mrem(10);
      min-height: mrem(60);
      margin: mrem(0);

      @include on-desktop {
          border-radius: rem(10);
          min-height: rem(60);
          margin: rem(0);
      }
  }

  .label.hold-place {
      top: mrem(17) !important;

      @include on-desktop {
          top: rem(17) !important;
      }
  }

  .mt-input.filled .label {
      top: -0.415rem;
      left: 0.36312rem;
  }

  .mt-input.filled {
      padding-left: mrem(14);

      @include on-desktop {
          padding-left: rem(14);
      }
  }

  .mt-input.active {
      padding-left: mrem(14);

      @include on-desktop {
          padding-left: rem(14);
      }
  }
}
// Animations
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
